import { FC, useMemo, useRef } from 'react';
import Table from '@components/new/Table/Table';
import HomeSectionFooter from '@pages/Home/components/HomeSectionFooter';
import AccessLink from "@components/AccessLink/AccessLink";
import { iTableConfigItem } from '@components/new/Table/Table.helpers';
import NewTooltip from '@components/new/NewTooltip/NewTooltip';
import useIsTruncated from '@hooks/useIsTruncated';
import { OnSiteChain } from '@apiHooks/useSalesOnSiteFocusData';
import { buildChainProfileUrl } from './HomeSalesFocusChains.helpers';
import {
    EMPTY_ON_SITE_CHAINS_MESSAGE,
    MORE_ON_SITE_TARGETS_LABEL,
    ON_SITE_C_STORE_TITLE,
    ON_SITE_GROCERY_TITLE,
    ON_SITE_LODGING_TITLE,
} from './HomeSalesFocusChains.constants';
import './SalesFocusOnSiteChainsTable.scss';

interface SalesFocusOnSiteChainsTableProps {
    cStore: OnSiteChain[];
    grocery: OnSiteChain[];
    lodging: OnSiteChain[];
    moreTargetsUrl: string;
}

const ON_SITE_UNITS_COLUMN_WIDTH = 80;

const TruncatedLinkCell: FC<{ text: string; href: string }> = ({ text, href }) => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const isTruncated = useIsTruncated(ref, text);
    return (
        <div className="HomeSalesFocusChains__truncated-cell">
            <a
                ref={ref}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="HomeSalesFocusChains__chain-link HomeSalesFocusChains__truncated-text"
            >
                {text}
            </a>
            {isTruncated && <NewTooltip position={['center', 'above']}>{text}</NewTooltip>}
        </div>
    );
};

interface SegmentTableProps {
    title: string;
    rows: OnSiteChain[];
}

const SegmentTable: FC<SegmentTableProps> = ({ title, rows }) => {
    const config: iTableConfigItem[] = useMemo(
        () => [
            {
                title: 'Chain',
                Value: ({ index }) => (
                    <TruncatedLinkCell
                        text={rows[index].chainName ?? ''}
                        href={buildChainProfileUrl(rows[index].chainId ?? 0)}
                    />
                ),
            },
            {
                title: 'Units In Area',
                width: ON_SITE_UNITS_COLUMN_WIDTH,
                textCentered: true,
                Value: ({ index }) => (
                    <span>{(rows[index].units ?? 0).toLocaleString()}</span>
                ),
            },
        ],
        [rows],
    );

    return (
        <div className="SalesFocusOnSiteChainsTable__column">
            <p className="SalesFocusOnSiteChainsTable__column-title">{title}</p>
            {rows.length === 0 ? (
                <div className="SalesFocusOnSiteChainsTable__empty">
                    {EMPTY_ON_SITE_CHAINS_MESSAGE}
                </div>
            ) : (
                <Table
                    config={config}
                    rowsCount={rows.length}
                    hasInnerScroll={false}
                    isCopyButtonHidden
                />
            )}
        </div>
    );
};

const SalesFocusOnSiteChainsTable: FC<SalesFocusOnSiteChainsTableProps> = ({
    cStore,
    grocery,
    lodging,
    moreTargetsUrl,
}) => (
    <div className="SalesFocusOnSiteChainsTable">
        <div className="SalesFocusOnSiteChainsTable__columns">
            <SegmentTable title={ON_SITE_C_STORE_TITLE} rows={cStore} />
            <SegmentTable title={ON_SITE_GROCERY_TITLE} rows={grocery} />
            <SegmentTable title={ON_SITE_LODGING_TITLE} rows={lodging} />
        </div>
        <HomeSectionFooter>
            <AccessLink
                url={moreTargetsUrl}
                text={MORE_ON_SITE_TARGETS_LABEL}
                onContactUsClick={() => undefined}
                testId="linkSalesFocusMoreOnSiteTargets"
            />
        </HomeSectionFooter>
    </div>
);

export default SalesFocusOnSiteChainsTable;
